<template>
    <page-controls>
        <template v-slot:left>
            <div class="@3xl/controls:w-1/2 @4xl/controls:max-w-md">
                <vx-filterable :active="active" @toggle="toggleFilterable">
                    <template v-slot:placeholder>
                        <span class="mr-2">Find</span>
                        <vx-badge v-if="filtersCount > 0" :borders="false" size="xs" @click.stop="reset">
                            {{ pluralized('filter', filtersCount, true) }}
                            <font-awesome-icon widthAuto :icon="faCircleXmark" />
                        </vx-badge>
                    </template>
                    <template v-slot:form>
                        <form @submit.prevent="apply" class="space-y-4">
                            <div>
                                <filter-label :canReset="!!filters.query" @reset="filters.query = undefined">
                                    Keyword Search
                                </filter-label>

                                <vx-input
                                    type="text"
                                    data-test="filters-query"
                                    placeholder="Search"
                                    maxlength="191"
                                    size="lg"
                                    v-model="filters.query"
                                >
                                    <template v-slot:right>
                                        <font-awesome-icon widthAuto class="text-xs" :icon="faMagnifyingGlass" />
                                    </template>
                                </vx-input>
                            </div>

                            <div>
                                <filter-label :canReset="filters.role !== undefined" @reset="filters.role = undefined">
                                    Role
                                </filter-label>

                                <vx-selectable
                                    data-test="filters-roles"
                                    v-model="filters.role"
                                    :reduce="(option) => option.value"
                                    :clearable="false"
                                    :options="roles"
                                    excludeSelected
                                    size="lg"
                                >
                                </vx-selectable>
                            </div>

                            <div class="flex gap-2 pt-4">
                                <vx-button type="button" color="muted-light" size="lg" @click="cancel"
                                    >Cancel</vx-button
                                >
                                <vx-button data-test="filters-apply" type="submit" size="lg" block>
                                    Apply
                                    <font-awesome-icon widthAuto :icon="faCircleCheck"></font-awesome-icon>
                                </vx-button>
                            </div>
                        </form>
                    </template>
                </vx-filterable>
            </div>
            <div class="@3xl/controls:w-1/2 @4xl/controls:w-56">
                <vx-button-group size="lg" class="w-full">
                    <vx-select
                        :modelValue="model.sort_field"
                        @update:modelValue="
                            updateModelValue('sort_field', $event);
                            emit('apply');
                        "
                        :options="props.sortOptions"
                    ></vx-select>
                    <vx-button
                        @click.prevent="
                            model.sort_dir === 'asc'
                                ? updateModelValue('sort_dir', 'desc')
                                : updateModelValue('sort_dir', 'asc');
                            emit('apply');
                        "
                        type="button"
                        color="white"
                        shadow
                        data-test="sort-direction"
                        :aria-label="`Sort ${
                            model.sort_dir === 'asc' ? 'ascending' : 'descending'
                        }. Press to toggle sort direction.`"
                    >
                        <font-awesome-icon
                            widthAuto
                            :icon="model.sort_dir === 'asc' ? faArrowUpWideShort : faArrowDownWideShort"
                        ></font-awesome-icon>
                    </vx-button>
                </vx-button-group>
            </div>
        </template>

        <template v-if="props.canManageUsers" v-slot:right>
            <slot name="buttons">
                <vx-button size="lg" data-test="add-user-btn" class="w-full" @click="emit('add')" color="primary">
                    Add User
                    <font-awesome-icon widthAuto :icon="faCirclePlus"></font-awesome-icon>
                </vx-button>
            </slot>
        </template>
    </page-controls>
</template>

<script setup>
import PageControls from '~/components/general/PageControls.vue';
import {
    faArrowDownWideShort,
    faArrowUpWideShort,
    faCircleCheck,
    faCirclePlus,
    faMagnifyingGlass,
    faCircleXmark,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    VxBadge,
    VxButton,
    VxButtonGroup,
    VxFilterable,
    VxInput,
    VxSelect,
    VxSelectable,
} from '@voxie/frontend-components';
import { computed, nextTick, ref, watch } from 'vue';
import { teamRolesOptions } from '~/constants/roles';
import FilterLabel from '~/components/general/FilterLabel.vue';
import { pluralized } from '~/components/filters';

const props = defineProps({
    type: {
        type: String,
        default: 'active',
    },
    sortOptions: {
        type: Array,
        default: () => [
            {
                value: 'id',
                label: 'Sort: Created',
            },
        ],
    },
    roleOptions: {
        type: Array,
    },
    canManageUsers: {
        type: Boolean,
        default: false,
    },
});

const filters = ref({});
const model = defineModel({});
const roles = computed(() => {
    return props.roleOptions.length > 0
        ? props.roleOptions
        : teamRolesOptions(props.type === 'pending' ? { exclude: ['owner'] } : {});
});

watch(
    () => model.value,
    (value) => {
        filters.value = { ...value };
    },
    {
        deep: true,
        immediate: true,
    }
);

const emit = defineEmits(['update:modelValue', 'apply', 'reset', 'add']);

const active = ref(false);

const toggleFilterable = (event) => {
    active.value = event;
};

const filtersCount = computed(() => (model.value.query ? 1 : 0) + (model.value.role ? 1 : 0));

const reset = () => {
    active.value = false;
    emit('reset');
};

const cancel = async () => {
    filters.value = { ...model.value };

    active.value = false;
};

const apply = async () => {
    active.value = false;
    emit('update:modelValue', { ...filters.value });
    await nextTick();
    emit('apply');
};

const updateModelValue = (key, value) => {
    emit('update:modelValue', {
        ...model.value,
        [key]: value,
    });
};
</script>
